import React, { useState, useEffect, useRef } from 'react'
import { DivProps } from '@commonTypes'
import { Card, H4, ButtonGroup, Elevation, Classes } from '@blueprintjs/core'
import { useSpring, animated, config } from 'react-spring'
import { Query, QueryResult } from 'react-apollo'
import { GridTableStatusEnum, IconButton, IGridTableColumn, NewGridTable } from '@ui'
import gql from 'graphql-tag'
import cx from 'classnames'
import bg from '../../assets/map-bg.png'
import WorldMap from './D3WorldMap'
import { legacyMode } from '../../App'
import { get } from 'lodash'
import { formatNumber } from './utils'
import track from '@track'
import css from './dashboard.module.scss'

const worldMapQuery = gql`
  query worldMap {
    viewer {
      company {
        geographicalLocations {
          cities {
            name
            lat
            long
          }
        }
      }
    }
  }
`

interface IMapDimensions {
  width: number
  height: number
}

interface ICity {
  name: string
  lat: number
  long: number
}

interface IPayload {
  viewer: {
    company: {
      geographicalLocations: {
        cities: ICity[]
      }
    }
  }
}

export default function WorldMapCard() {
  const dimensions = useMapDimensions()
  const mapContainer = useRef<HTMLDivElement | null>(null)
  const [cities, setCities] = useState<ICity[]>([])
  const worldMap = useRef<any>(null)
  const [showCityDetailsCard, setShowCityDetailsCard] = useState(false)
  const [detailCities, setDetailCities] = useState<string[]>([])
  const animatedStyles = useSpring({
    opacity: showCityDetailsCard ? 1 : 0,
    transform: showCityDetailsCard ? 'translateY(0px)' : 'translateY(10px)',
    config: config.stiff,
  })

  useEffect(() => {
    if (!dimensions) return

    const node = mapContainer.current

    if (worldMap.current) {
      worldMap.current.update({
        dimensions,
        cities,
        node,
      })
    } else {
      worldMap.current = new WorldMap({
        dimensions,
        cities,
        node,
        onCityClick: (cities: ICity[]) => {
          setShowCityDetailsCard(true)
          setDetailCities(cities.map((c) => c.name))
          track.dashboard({
            eventName: track.Event.WORLD_MAP_OBJECT_CLICK,
          })
        },
      })
    }

    worldMap.current.draw()
  }, [dimensions, cities])

  return (
    <Card style={{ overflow: 'hidden' }}>
      <H4>Map</H4>
      <Query notifyOnNetworkStatusChange query={worldMapQuery}>
        {({ loading, data }: QueryResult<IPayload>) => {
          if (!loading && data) {
            setCities(data.viewer.company.geographicalLocations.cities)
          }

          return (
            <Container ref={mapContainer}>
              {worldMap.current && (
                <div className={css.world_map_zoom_widget}>
                  <ButtonGroup vertical={true}>
                    <IconButton iconName="plus" onClick={() => worldMap.current.zoomIn()} />
                    <IconButton iconName="minus" onClick={() => worldMap.current.zoomOut()} />
                  </ButtonGroup>
                  <div className={css.world_map_help_text}>Use Ctrl + scroll to zoom the map</div>
                </div>
              )}
              {showCityDetailsCard && (
                <animated.div
                  style={{
                    ...animatedStyles,
                    position: 'absolute',
                    left: 42,
                    bottom: 0,
                    right: 0,
                    top: '60%',
                  }}
                >
                  <CityDetails
                    close={() => {
                      setShowCityDetailsCard(false)
                      setDetailCities([])
                    }}
                    cities={detailCities}
                  />
                </animated.div>
              )}
            </Container>
          )
        }}
      </Query>
    </Card>
  )
}

function useMapDimensions(): IMapDimensions | null {
  const [dimensions, setDimensions] = useState<IMapDimensions | null>(null)

  function updateDimensions(): void {
    const containerMargin = 15 * 2
    const cardPadding = 20 * 2
    const navWidth = legacyMode ? 0 : 200

    const width =
      document.body.clientWidth > 1700
        ? 1700 - containerMargin - cardPadding
        : document.body.clientWidth - containerMargin - cardPadding - navWidth
    const height = width / 2

    setDimensions({ width, height })
  }

  useEffect(updateDimensions, [])

  useEffect(() => {
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  return dimensions
}

const cityDetailsQuery = gql`
  query cityDetails($cities: [String]) {
    viewer {
      company {
        geographicalLocations(cities: $cities) {
          cities {
            name
            buildings {
              id
              name
              counters {
                employees
                floors
                sqft
                density
                unallocated
                unoccupied
                capacity
              }
            }
          }
        }
      }
    }
  }
`

interface ITableBuilding {
  id: string
  name: string
  counters: {
    floors: number
    employees: number
    sqft: number
    density: number
    unallocated: number
    unoccupied: number
    capacity: number
  }
}

interface ITableCity {
  name: string
  buildings: ITableBuilding[]
}

interface ICityPayload {
  viewer: {
    company: {
      geographicalLocations: {
        cities: ITableCity[]
      }
    }
  }
}

function CityDetails({ close, cities }: { close: () => void; cities: string[] }) {
  return (
    <Card elevation={Elevation.TWO} style={{ height: '100%', position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 100,
          background: 'white',
        }}
      >
        <IconButton minimal iconName="times" tooltip="Close" onClick={close} />
      </div>
      <div className={css.world_map_city_content_wrapper}>
        <Query query={cityDetailsQuery} variables={{ cities }}>
          {({ data, loading }: QueryResult<ICityPayload>) => {
            if (!data || !data.viewer || loading) {
              return (
                <div>
                  <div style={{ marginBottom: 10, height: 30 }} className={Classes.SKELETON} />
                  <div style={{ height: 100 }} className={Classes.SKELETON} />
                </div>
              )
            }
            const { cities: payloadCities } = data.viewer.company.geographicalLocations
            return (
              <>
                {payloadCities.map((city, i) => (
                  <CityTable key={`city-table-${i}`} city={city} />
                ))}
              </>
            )
          }}
        </Query>
      </div>
    </Card>
  )
}

function CityTable({ city }: { city: ITableCity }) {
  const { buildings } = city

  const columns: IGridTableColumn<ITableBuilding>[] = [
    {
      label: 'Building',
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{cell.data.name}</div>
      },
    },
    {
      label: 'Floors',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{formatNumber(get(cell.data, 'counters.floors'))}</div>
      },
    },
    {
      label: 'Workstations',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{formatNumber(get(cell.data, 'counters.capacity'))}</div>
      },
    },
    {
      label: 'SqFt.',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{formatNumber(get(cell.data, 'counters.sqft'))}</div>
      },
    },
    {
      label: 'Unoccupied',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{formatNumber(get(cell.data, 'counters.unoccupied'))}</div>
      },
    },
    {
      label: 'Unallocated',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{formatNumber(get(cell.data, 'counters.unallocated'))}</div>
      },
    },
    {
      label: 'Density',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{formatNumber(get(cell.data, 'counters.density'))}</div>
      },
    },
  ]

  const formatBuildings = (items: ITableBuilding[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formatedBuildings = formatBuildings(buildings)

  return (
    <div
      style={{
        marginBottom: 30,
      }}
    >
      <H4>{city.name}</H4>
      <div
        style={{
          border: '1px solid #eee',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <NewGridTable<ITableBuilding>
          sticky={false}
          status={GridTableStatusEnum.IDLE}
          columns={columns}
          data={formatedBuildings}
        />
      </div>
    </div>
  )
}

const Container = React.forwardRef<HTMLDivElement, DivProps>(
  ({ className, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cx(css.world_map_container, className)}
        style={{
          backgroundImage: `url(${bg})`,
          ...style,
        }}
        {...props}
      />
    )
  }
)
